import type {
  OdooResourceType,
  OdooSearchItem,
  ManualLinkResult,
  CreateInOdooResult,
  UnlinkedOdooResource,
} from '@/types/odoo.types';
import { ApiService } from './api.service';
import { endpoints } from './endpoints';

export class OdooApiService extends ApiService {
  // ─── Search ────────────────────────────────────────────────────────────

  /** Search Odoo resources by name. */
  searchOdoo = async (
    entity: OdooResourceType,
    query?: string,
    limit = 10
  ): Promise<OdooSearchItem[]> =>
    this.get(endpoints.odooLink.searchOdoo(entity), {
      params: { query, limit },
    });

  // ─── Link ──────────────────────────────────────────────────────────────

  /** Link a Hub resource to an existing Odoo record. */
  link = async (entity: OdooResourceType, id: string, odooId: number): Promise<ManualLinkResult> =>
    this.patch(endpoints.odooLink.link(entity, id), { odooId });

  // ─── Unlink ────────────────────────────────────────────────────────────

  /** Remove the link between a Hub resource and its Odoo record. */
  unlink = async (entity: OdooResourceType, id: string): Promise<ManualLinkResult> =>
    this.patch(endpoints.odooLink.unlink(entity, id));

  // ─── Push to Odoo ──────────────────────────────────────────────────────

  /** Create the Hub resource in Odoo and auto-link it. */
  pushToOdoo = async (entity: OdooResourceType, id: string): Promise<CreateInOdooResult> =>
    this.post(endpoints.odooLink.pushToOdoo(entity, id));

  // ─── Unlinked ──────────────────────────────────────────────────────────

  /** Get Hub resources that are not yet linked to Odoo. */
  getUnlinked = async (
    entity: OdooResourceType,
    query?: string,
    limit = 50,
    offset = 0
  ): Promise<UnlinkedOdooResource> =>
    this.get(endpoints.odooLink.unlinked(entity), { params: { query, limit, offset } });
}

// Singleton instance
export const odooApiService = new OdooApiService();

// Named exports for convenience
export const {
  searchOdoo,
  link: linkOdoo,
  unlink: unlinkOdoo,
  pushToOdoo,
  getUnlinked,
} = odooApiService;
